/**
 * useCelebrate — fire a confetti burst on user wins (Diamant #3).
 *
 * Premium feel : confetti burst aux moments clés (1er audit, 1er RDV, 1er
 * recrutement). Each celebration is keyed (e.g. 'first-audit') so we only
 * fire ONCE per user per moment — stored in localStorage.
 *
 * Usage :
 *   const celebrate = useCelebrate();
 *   celebrate('first-audit');  // bursts only the first time
 */
import { useCallback } from 'react';
import confetti from 'canvas-confetti';

type CelebrationKey =
  | 'first-audit'
  | 'first-rdv'
  | 'first-recruit'
  | 'first-prospect-hot'
  | 'streak-7';

const STORAGE_KEY = 'nia-celebrations';

function loadFired(): Record<string, true> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function markFired(key: CelebrationKey): void {
  const fired = loadFired();
  fired[key] = true;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fired));
}

/** Fires a single confetti burst from the center of the viewport. */
function burst(): void {
  // Burst principal — gradient Network-iA (bleu → violet → rose)
  confetti({
    particleCount: 90,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#3b82f6', '#a855f7', '#ec4899'],
  });
  // Second burst un peu décalé pour un effet "feu d'artifice"
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors: ['#3b82f6', '#a855f7', '#ec4899'],
    });
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors: ['#3b82f6', '#a855f7', '#ec4899'],
    });
  }, 250);
}

export function useCelebrate() {
  return useCallback((key: CelebrationKey, opts?: { force?: boolean }) => {
    const fired = loadFired();
    if (!opts?.force && fired[key]) return;
    burst();
    markFired(key);
  }, []);
}
